import { buildOpcodeTable, type OpcodeEntry } from './opcode-table';
import { H_TI, H_ST, H_IA, H_IE, H_DW } from './instructions/constants';

export interface DisassembledInstruction {
  address: number;
  csr: number;
  bytes: string;
  mnemonic: string;
  operands: string;
  length: number;
}

const BC_COND = ['GE', 'LT', 'GT', 'LE', 'GES', 'LTS', 'GTS', 'LES', 'NE', 'EQ', 'NV', 'OV', 'PS', 'NS', 'AL', ''];

let _opcodeTable: Map<number, OpcodeEntry> | null = null;

function getOpcodeTable(): Map<number, OpcodeEntry> {
  if (!_opcodeTable) {
    _opcodeTable = buildOpcodeTable();
  }
  return _opcodeTable;
}

function wordToBytes(w: number): [number, number] {
  return [w & 0xFF, (w >> 8) & 0xFF];
}

function formatBytes(words: number[], count: number): string {
  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const w = words[i] !== undefined ? words[i] : 0;
    const [lo, hi] = wordToBytes(w);
    parts.push(lo.toString(16).padStart(2, '0').toUpperCase());
    parts.push(hi.toString(16).padStart(2, '0').toUpperCase());
  }
  return parts.join(' ');
}

function regName(regSize: number, value: number): string {
  switch (regSize) {
    case 1: return `R${value}`;
    case 2: return `ER${value}`;
    case 4: return `XR${value}`;
    case 8: return `QR${value}`;
    default: return `R${value}`;
  }
}

function cRegName(regSize: number, value: number): string {
  switch (regSize) {
    case 1: return `CR${value}`;
    case 2: return `CER${value}`;
    case 4: return `CXR${value}`;
    case 8: return `CQR${value}`;
    default: return `CR${value}`;
  }
}

function signedHex(val: number, bits: number): string {
  const hexlen = Math.ceil(bits / 4);
  const mask = (1 << bits) - 1;
  val &= mask;
  const signBit = 1 << (bits - 1);
  if (val & signBit) {
    const neg = ((~val + 1)) & mask;
    return `-0x${neg.toString(16).padStart(hexlen, '0').toUpperCase()}`;
  }
  return `0x${val.toString(16).padStart(hexlen, '0').toUpperCase()}`;
}

function formatImm(regSize: number, hint: number, val: number): string {
  if (regSize === 2 && (hint & H_IE)) {
    val |= (val & 0x40) ? 0xFF80 : 0;
  }
  val &= 0xFFFF;
  const bits = regSize === 1 ? 8 : (hint & H_IE) ? 7 : 16;
  return `#${signedHex(val, bits)}`;
}

function formatAbsAddr(longImm: number): string {
  return `0x${(longImm & 0xFFFF).toString(16).padStart(4, '0').toUpperCase()}`;
}

function formatLongAddr(seg: number, offset: number): string {
  const s = seg.toString(16).toUpperCase();
  const o = (offset & 0xFFFF).toString(16).padStart(4, '0').toUpperCase();
  return `0x${s}:0x${o}`;
}

function decodeInstruction(
  opcode: number,
  instrAddr: number,
  entry: OpcodeEntry,
  hint: number,
  longImm: number
): [string, string] {
  const ops = entry.operands;
  const op0 = ops[0];
  const op1 = ops[1];
  const op0Val = (opcode >> op0.shift) & op0.mask;
  const op1Val = (opcode >> op1.shift) & op1.mask;
  const handlerName = entry.handler.name;

  switch (handlerName) {
    case 'opADD': {
      if (op0.register_size && op1.register_size)
        return ['ADD', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['ADD', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, 0, op1Val)}`];
    }
    case 'opADDC': {
      if (op0.register_size && op1.register_size)
        return ['ADDC', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['ADDC', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, 0, op1Val)}`];
    }
    case 'opADD16': {
      if (op0.register_size && op1.register_size)
        return ['ADD', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['ADD', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, hint, op1Val)}`];
    }
    case 'opSUB': {
      if (op0.register_size && op1.register_size)
        return ['CMP', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['CMP', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, 0, op1Val)}`];
    }
    case 'opSUBC': {
      if (op0.register_size && op1.register_size)
        return ['CMPC', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['CMPC', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, 0, op1Val)}`];
    }
    case 'opAND': {
      if (op0.register_size && op1.register_size)
        return ['AND', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['AND', `${regName(op0.register_size, op0Val)}, ${formatImm(op0.register_size, 0, op1Val)}`];
    }
    case 'opSLL': {
      if (op0.register_size && op1.register_size)
        return ['SLL', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['SLL', `${regName(op0.register_size, op0Val)}, #${op1Val}`];
    }
    case 'opSLLC': {
      if (op0.register_size && op1.register_size)
        return ['SLLC', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['SLLC', `${regName(op0.register_size, op0Val)}, #${op1Val}`];
    }
    case 'opSRA': {
      if (op0.register_size && op1.register_size)
        return ['SRA', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['SRA', `${regName(op0.register_size, op0Val)}, #${op1Val}`];
    }
    case 'opSRL': {
      if (op0.register_size && op1.register_size)
        return ['SRL', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['SRL', `${regName(op0.register_size, op0Val)}, #${op1Val}`];
    }
    case 'opSRLC': {
      if (op0.register_size && op1.register_size)
        return ['SRLC', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
      return ['SRLC', `${regName(op0.register_size, op0Val)}, #${op1Val}`];
    }
    case 'opLS_EA': {
      const isST = !!(hint & H_ST);
      const isIA = !!(hint & H_IA);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      if (isST)
        return ['ST', `${rn}, [EA${isIA ? '+' : ''}]`];
      return ['L', `${rn}, [EA${isIA ? '+' : ''}]`];
    }
    case 'opLS_R': {
      const isST = !!(hint & H_ST);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      const erm = regName(2, op1Val);
      if (isST)
        return ['ST', `${rn}, [${erm}]`];
      return ['L', `${rn}, [${erm}]`];
    }
    case 'opLS_I_R': {
      const isST = !!(hint & H_ST);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      const erm = regName(2, op1Val);
      const addr = signedHex(longImm, 16);
      if (isST)
        return ['ST', `${rn}, ${addr}[${erm}]`];
      return ['L', `${rn}, ${addr}[${erm}]`];
    }
    case 'opLS_BP': {
      const isST = !!(hint & H_ST);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      let disp = op1Val;
      disp |= (disp & 0x20) ? 0xFFC0 : 0;
      disp &= 0xFFFF;
      if (isST)
        return ['ST', `${rn}, ${signedHex(disp, 6)}h[BP]`];
      return ['L', `${rn}, ${signedHex(disp, 6)}h[BP]`];
    }
    case 'opLS_FP': {
      const isST = !!(hint & H_ST);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      let disp = op1Val;
      disp |= (disp & 0x20) ? 0xFFC0 : 0;
      disp &= 0xFFFF;
      if (isST)
        return ['ST', `${rn}, ${signedHex(disp, 6)}h[FP]`];
      return ['L', `${rn}, ${signedHex(disp, 6)}h[FP]`];
    }
    case 'opLS_I': {
      const isST = !!(hint & H_ST);
      const sz = hint >> 8;
      const rn = regName(sz, op0Val);
      if (isST)
        return ['ST', `${rn}, ${formatAbsAddr(longImm)}`];
      return ['L', `${rn}, ${formatAbsAddr(longImm)}`];
    }
    case 'opPUSH': {
      return ['PUSH', `${regName(op1.register_size, op1Val)}`];
    }
    case 'opPUSHL': {
      const val = op1Val;
      const parts: string[] = [];
      if (val & 2) parts.push('ELR');
      if (val & 4) parts.push('EPSW');
      if (val & 8) parts.push('LR');
      if (val & 1) parts.push('EA');
      return ['PUSH', parts.join(', ')];
    }
    case 'opPOP': {
      return ['POP', `${regName(op0.register_size, op0Val)}`];
    }
    case 'opPOPL': {
      const val = op0Val;
      const parts: string[] = [];
      if (val & 2) parts.push('PC');
      if (val & 4) parts.push('PSW');
      if (val & 8) parts.push('LR');
      if (val & 1) parts.push('EA');
      return ['POP', parts.join(', ')];
    }
    case 'opBC': {
      const condIdx = (opcode >> 8) & 0x000F;
      const cond = BC_COND[condIdx] || '';
      let offset = op0Val;
      offset |= (offset & 0x80) ? 0x7F00 : 0;
      offset &= 0xFFFF;
      const target = (instrAddr + 2 + (offset << 1)) & 0xFFFFF;
      return ['BC', `${cond}, 0x${target.toString(16).toUpperCase()}`];
    }
    case 'opB': {
      if (hint & H_TI)
        return ['B', `${formatLongAddr(op1Val, longImm)}`];
      return ['B', `${regName(2, op1Val)}`];
    }
    case 'opBL': {
      if (hint & H_TI)
        return ['BL', `${formatLongAddr(op1Val, longImm)}`];
      return ['BL', `${regName(2, op1Val)}`];
    }
    case 'opRT': return ['RT', ''];
    case 'opRTI': return ['RTI', ''];
    case 'opBITMOD': {
      const bit = op1Val;
      if (hint & H_TI) {
        const addr = formatAbsAddr(longImm);
        switch (opcode & 0x000F) {
          case 0: return ['SB', `${addr}.${bit}`];
          case 1: return ['TB', `${addr}.${bit}`];
          case 2: return ['RB', `${addr}.${bit}`];
          default: return ['SB', `${addr}.${bit}`];
        }
      }
      switch (opcode & 0x000F) {
        case 0: return ['SB', `${regName(1, op0Val)}.${bit}`];
        case 1: return ['TB', `${regName(1, op0Val)}.${bit}`];
        case 2: return ['RB', `${regName(1, op0Val)}.${bit}`];
        default: return ['SB', `${regName(1, op0Val)}.${bit}`];
      }
    }
    case 'opDAA': return ['DAA', `${regName(1, op0Val)}`];
    case 'opDAS': return ['DAS', `${regName(1, op0Val)}`];
    case 'opNEG': return ['NEG', `${regName(1, op0Val)}`];
    case 'opEXTBW': {
      const nIdx = (opcode & 0x00E0) >> 4;
      return ['EXTBW', `ER${nIdx}`];
    }
    case 'opSWI': return ['SWI', `#${op0Val}`];
    case 'opBRK': return ['BRK', ''];
    case 'opNOP': return ['NOP', ''];
    case 'opADDSP': {
      let val = op0Val;
      val |= (val & 0x80) ? 0xFF00 : 0;
      val &= 0xFFFF;
      return ['ADD', `SP, ${signedHex(val, 8)}`];
    }
    case 'opLEA': {
      if (hint & H_TI) {
        if (op1.register_size)
          return ['LEA', `${signedHex(longImm, 16)}[${regName(op1.register_size, op1Val)}]`];
        return ['LEA', `${formatAbsAddr(longImm)}`];
      }
      return ['LEA', `[${regName(op1.register_size, op1Val)}]`];
    }
    case 'opINC_EA': return ['INC', '[EA]'];
    case 'opDEC_EA': return ['DEC', '[EA]'];
    case 'opCPLC': return ['CPLC', ''];
    case 'opMUL': {
      return ['MUL', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
    }
    case 'opDIV': {
      return ['DIV', `${regName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
    }
    case 'opPSW_OR': {
      if ((opcode & 0xFF) === 0x08) return ['EI', ''];
      return ['SC', ''];
    }
    case 'opPSW_AND': {
      if ((opcode & 0xFF) === 0xF7) return ['DI', ''];
      return ['RC', ''];
    }
    case 'opDSR': {
      if (hint & H_DW) {
        if (op0.register_size)
          return ['DSR', `${regName(op0.register_size, op0Val)}`];
        return ['DSR', `#${signedHex(op0Val, 8)}`];
      }
      return ['DSR', ''];
    }
    case 'opCTRL': {
      const ctrlType = hint >> 8;
      switch (ctrlType) {
        case 1: return ['MOV', `ECSR, ${regName(op1.register_size, op1Val)}`];
        case 2: return ['MOV', `ELR, ${regName(op1.register_size, op1Val)}`];
        case 3: return ['MOV', `EPSW, ${regName(op1.register_size, op1Val)}`];
        case 4: return ['MOV', `${regName(op0.register_size, op0Val)}, ELR`];
        case 5: return ['MOV', `${regName(op0.register_size, op0Val)}, SP`];
        case 6: return ['MOV', `PSW, ${regName(op1.register_size, op1Val)}`];
        case 7: return ['MOV', `PSW, ${formatImm(op1.register_size, 0, op1Val)}`];
        case 8: return ['MOV', `${regName(op0.register_size, op0Val)}, ECSR`];
        case 9: return ['MOV', `${regName(op0.register_size, op0Val)}, EPSW`];
        case 10: return ['MOV', `${regName(op0.register_size, op0Val)}, PSW`];
        case 11: return ['MOV', `SP, ${regName(op1.register_size, op1Val)}`];
        default: return ['MOV', ''];
      }
    }
    case 'opCR_R': {
      if (hint & H_ST)
        return ['MOV', `${regName(op0.register_size, op0Val)}, ${cRegName(op1.register_size, op1Val)}`];
      return ['MOV', `${cRegName(op0.register_size, op0Val)}, ${regName(op1.register_size, op1Val)}`];
    }
    case 'opCR_EA': {
      const isST = !!(hint & H_ST);
      const isIA = !!(hint & H_IA);
      const sz = hint >> 8;
      if (isST) {
        const crnVal = (opcode >> op0.shift) & op0.mask;
        return ['MOV', `[EA${isIA ? '+' : ''}], ${cRegName(sz, crnVal)}`];
      }
      const crnVal = (opcode >> op1.shift) & op1.mask;
      return ['MOV', `${cRegName(sz, crnVal)}, [EA${isIA ? '+' : ''}]`];
    }
    default:
      return ['DW', `0x${(opcode & 0xFFFF).toString(16).padStart(4, '0').toUpperCase()}`];
  }
}

export function disassembleOne(
  readWord: (csr: number, address: number) => number,
  csr: number,
  address: number
): DisassembledInstruction {
  const word0 = readWord(csr, address) & 0xFFFF;
  const entry = getOpcodeTable().get(word0);

  if (!entry || entry.handler.name === '') {
    return {
      address,
      csr,
      bytes: formatBytes([word0], 1),
      mnemonic: 'DW',
      operands: `0x${word0.toString(16).padStart(4, '0').toUpperCase()}`,
      length: 2,
    };
  }

  const hint = entry.hint;
  const hasLongImm = !!(hint & H_TI);
  let longImm = 0;
  let numWords = 1;

  if (hasLongImm) {
    longImm = readWord(csr, (address + 2) & 0xFFFF) & 0xFFFF;
    numWords = 2;
  }

  const [mnemonic, operands] = decodeInstruction(word0, address, entry, hint, longImm);

  const words: number[] = [word0];
  if (numWords > 1) words.push(longImm);

  return {
    address,
    csr,
    bytes: formatBytes(words, numWords),
    mnemonic,
    operands,
    length: numWords * 2,
  };
}

export function disassembleRange(
  readWord: (csr: number, address: number) => number,
  csr: number,
  address: number,
  count: number
): DisassembledInstruction[] {
  const result: DisassembledInstruction[] = [];
  let addr = address;
  for (let i = 0; i < count; i++) {
    const inst = disassembleOne(readWord, csr, addr);
    result.push(inst);
    addr = (addr + inst.length) & 0xFFFF;
  }
  return result;
}
